import { Skeleton, Stack } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { Outlet, useParams } from "react-router";
import { HeaderLayout } from "../../../components";
import { useGetUser } from "../../../http";
import { IUserDetail } from "../../../models";
import { setTitlePrefix } from "../../../utilities";
import { UserActions, UserNavBar, UserTitle } from "./components";

interface IParams {
  userId: string;
}

interface IUserPageLayoutContext {
  user: IUserDetail;
}

const UserPageLayoutContext = React.createContext<IUserPageLayoutContext | undefined>(undefined);

const UserPageLayout = () => {
  const { userId } = useParams<keyof IParams>() as IParams;
  const { data: user, isLoading } = useGetUser(userId);

  useEffect(() => {
    setTitlePrefix(user?.displayName);
  }, [user?.displayName]);

  if (isLoading) {
    return (
      <HeaderLayout
        title={
          <Stack spacing={0.25}>
            <Skeleton variant="rectangular" height="25px" width="60px" />
            <Skeleton variant="rectangular" height="40px" width="300px" />
          </Stack>
        }
      >
        <Stack spacing={1}>
          <Skeleton variant="rectangular" width="75%" />
          <Skeleton variant="rectangular" />
          <Skeleton variant="rectangular" width="50%" />
        </Stack>
      </HeaderLayout>
    );
  }

  if (!isLoading && user) {
    return (
      <UserPageLayoutContext.Provider
        value={{
          user,
        }}
      >
        <HeaderLayout
          title={
            <Stack spacing={0.25}>
              <UserTitle />
            </Stack>
          }
          actions={<UserActions />}
          navbar={<UserNavBar />}
          headerContainerProps={{
            sx: {
              alignItems: "flex-end",
              pb: 0,
            },
          }}
        >
          <Outlet />
        </HeaderLayout>
      </UserPageLayoutContext.Provider>
    );
  }

  return null;
};

const useUserPageLayoutContext = () => {
  const context = useContext(UserPageLayoutContext);

  if (context === undefined) {
    throw new Error("UserRouteContext is not initialized.");
  }

  return context;
};

export { UserPageLayout, useUserPageLayoutContext };
